// frontend/src/components/Admin/EnrollPage.tsx
// MP-STAB-04 — Página PÚBLICA de canje de Device Enrollment.
// El destinatario abre el Enrollment Link desde el dispositivo que desea
// registrar. El link (ENR-xxx, 128+ bits) ES la credencial — no se pide ningún código.
// El canje consume internamente el Contrato de Registro de Pairing (backend).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../../services/api';
import * as deviceStore from '../../services/deviceStore';

export default function EnrollPage() {
  const { id } = useParams<{ id: string }>();
  const [info, setInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.resolveEnrollment(id)
      .then((data) => {
        if (data && data.enrollment) setInfo(data.enrollment);
        else setError('Esta solicitud de registro no existe o ya no está disponible.');
      })
      .catch(() => setError('No se pudo cargar la solicitud. Revisa el enlace e inténtalo de nuevo.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleRedeem = async () => {
    if (!id || redeeming) return;
    setRedeeming(true);
    setError('');
    try {
      const data = await api.redeemEnrollment(id, info?.label || '');
      if (data && data.deviceId && data.deviceSecret) {
        // Guardar credenciales → auto-login T3 → el dispositivo queda autorizado
        deviceStore.save({ deviceId: data.deviceId, deviceSecret: data.deviceSecret });
        // KF25-AUTH-01/02 (C1): notificar al Resolver (App.tsx) — re-consulta al
        // backend para reflejar la nueva autoridad sin esperar navegación/recarga.
        window.dispatchEvent(new CustomEvent('kf25:device-registered'));
        setDone(true);
      } else {
        setError('La solicitud ya fue utilizada o expiró.');
      }
    } catch (e: any) {
      setError(e.message || 'No se pudo completar el registro.');
    } finally {
      setRedeeming(false);
    }
  };

  // Estilos mínimos con Tailwind (ya en el bundle)
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
        {loading && <p className="text-center text-gray-500">Cargando solicitud…</p>}

        {!loading && error && !info && (
          <div className="text-center">
            <div className="text-4xl mb-3">🔗</div>
            <h1 className="text-lg font-semibold text-gray-800 mb-2">Solicitud no disponible</h1>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        )}

        {!loading && info && !done && (
          <div className="text-center">
            <div className="text-4xl mb-3">📱</div>
            <h1 className="text-lg font-semibold text-gray-800 mb-1">Registrar este dispositivo</h1>
            <p className="text-sm text-gray-500 mb-4">
              Solicitud para <span className="font-medium text-gray-700">{info.adminName}</span>
            </p>
            {info.status !== 'pending' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-700">
                  {info.status === 'redeemed'
                    ? 'Esta solicitud ya fue utilizada en otro dispositivo.'
                    : info.status === 'expired'
                      ? 'Esta solicitud expiró. Pide un enlace nuevo.'
                      : 'Esta solicitud ya no está activa.'}
                </p>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                  <p className="text-xs text-gray-400 mb-1">Expira</p>
                  <p className="text-sm font-medium text-gray-700">
                    {new Date(info.expiresAt).toLocaleTimeString()}
                  </p>
                </div>
                <button
                  onClick={handleRedeem}
                  disabled={redeeming}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
                >
                  {redeeming ? 'Registrando…' : 'Registrar este dispositivo'}
                </button>
                <p className="text-xs text-gray-400 mt-3">
                  Al registrarte, este equipo queda autorizado para {info.adminName}. La solicitud se
                  inutiliza inmediatamente.
                </p>
              </>
            )}
          </div>
        )}

        {done && (
          <div className="text-center">
            <div className="text-4xl mb-3">✅</div>
            <h1 className="text-lg font-semibold text-gray-800 mb-2">¡Dispositivo registrado!</h1>
            <p className="text-sm text-gray-500 mb-4">
              Este equipo ya está autorizado. Puedes cerrar esta ventana y volver a la tienda.
            </p>
            <a
              href="/"
              className="inline-block bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-6 rounded-xl transition-colors"
            >
              Ir a la tienda
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
